/**
 * useResolvedCtas — consume /api/cta-resolved en BaseTemplate (y otros bio templates).
 *
 * Un fetch al mount; los overrides pisan defaults (env) vía resolveField().
 *
 * Geo: pasamos ?country=&province=&city= desde getServerTemplateConfig()
 * (window.__TEMPLATE_CONFIG__ inyectado por el Worker). Así el resolver
 * coincide con la misma provincia/ciudad que el Worker usó para hideCta /
 * showWishlist / broadcast, aunque Railway solo exponga headers genéricos.
 *
 * Si el tráfico pasa por el Worker, el origin también recibe X-User-Province
 * y X-User-City en la petición; las query siguen teniendo prioridad cuando
 * van presentes (mismo snapshot que __TEMPLATE_CONFIG__).
 */

import { useEffect, useState } from "react";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
import { appendGeoSimToSearchParams } from "@/lib/geoSim/session";

export type CtaCategory =
  | "telegram_basic"
  | "vip_membership"
  | "free_channel"
  | "special_offer"
  | "video"
  | "whatsapp_broadcast"
  | "instagram_membership"
  | "talk"
  | "group_talk";

export interface CtaResolvedResponse {
  brand: string;
  version: number;
  geo: {
    country?: string | null;
    province?: string | null;
    city?: string | null;
  };
  ctas: Partial<Record<CtaCategory, Record<string, string>>>;
}

export interface UseResolvedCtasResult {
  ctas: Partial<Record<CtaCategory, Record<string, string>>>;
  ready: boolean;
  version: number;
  geo: CtaResolvedResponse["geo"];
}

const EMPTY_CTAS: Partial<Record<CtaCategory, Record<string, string>>> = {};
const EMPTY_GEO = { country: null, province: null, city: null };

export function useResolvedCtas(): UseResolvedCtasResult {
  const [ctas, setCtas] =
    useState<Partial<Record<CtaCategory, Record<string, string>>>>(EMPTY_CTAS);
  const [ready, setReady] = useState(false);
  const [version, setVersion] = useState(0);
  const [geo, setGeo] = useState<CtaResolvedResponse["geo"]>(EMPTY_GEO);

  useEffect(() => {
    if (typeof window === "undefined") {
      setReady(true);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const tc = getServerTemplateConfig();
        const params = new URLSearchParams();
        if (tc?.country) params.set("country", tc.country);
        if (tc?.province) params.set("province", tc.province);
        if (tc?.city) params.set("city", tc.city);
        // Geo-sim poll: reenviar flag/token/vpn para no pisar con geo real del operador
        appendGeoSimToSearchParams(params);
        const qs = params.toString();
        const url = qs ? `/api/cta-resolved?${qs}` : "/api/cta-resolved";
        const res = await fetch(url, { method: "GET", cache: "no-store" });
        if (!res.ok) {
          if (!cancelled) setReady(true);
          return;
        }
        const data = (await res.json()) as CtaResolvedResponse;
        if (cancelled) return;
        setCtas(data.ctas ?? EMPTY_CTAS);
        setVersion(typeof data.version === "number" ? data.version : 0);
        setGeo(data.geo ?? EMPTY_GEO);
      } catch {
        /* fail-open */
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { ctas, ready, version, geo };
}

export function resolveField<T>(
  categoryFields: Record<string, string> | undefined,
  field: string,
  fallback: T,
  parser?: (raw: string) => T,
): T {
  if (!categoryFields) return fallback;
  const raw = categoryFields[field];
  if (raw == null || raw === "") return fallback;
  if (parser) {
    try {
      return parser(raw);
    } catch {
      return fallback;
    }
  }
  return raw as unknown as T;
}

export function resolveBoolField(
  categoryFields: Record<string, string> | undefined,
  field: string,
  fallback: boolean,
): boolean {
  return resolveField<boolean>(categoryFields, field, fallback, (raw) => {
    const t = raw.trim().toLowerCase();
    if (t === "true" || t === "1" || t === "yes") return true;
    if (t === "false" || t === "0" || t === "no") return false;
    return fallback;
  });
}

export function resolveNumField(
  categoryFields: Record<string, string> | undefined,
  field: string,
  fallback: number,
): number {
  return resolveField<number>(categoryFields, field, fallback, (raw) => {
    const n = Number(raw);
    return Number.isFinite(n) ? n : fallback;
  });
}
